import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";
import {
  apiErrorMessage,
  apiFieldErrors,
  MAX_LENGTHS,
  validateSettings,
} from "../lib/companyValidation";
import { notifyBrandingChanged } from "../lib/branding";
import Layout from "../components/Layout";
import CompanyField from "../components/company/CompanyField";
import CompanyLogoCard from "../components/company/CompanyLogoCard";
import ReminderScheduleCard from "../components/company/ReminderScheduleCard";
import { sameOffsets, validateOffsets } from "../lib/reminderSettings";

const BASIC_FIELDS = ["name", "legal_name", "eik", "vat_number", "default_currency"];
const CONTACT_FIELDS = ["phone", "website", "address"];
const EMAIL_FIELDS = ["reply_to_email", "email_signature"];
// Phase 3B — automatic quotation numbering. The counter itself is deliberately
// NOT editable: only the prefix is a setting.
const NUMBERING_FIELDS = ["quotation_number_prefix"];

const draftFrom = (settings) => {
  const out = {};
  [...BASIC_FIELDS, ...CONTACT_FIELDS, ...EMAIL_FIELDS, ...NUMBERING_FIELDS].forEach((f) => {
    out[f] = settings?.[f] ?? "";
  });
  return out;
};

export default function Settings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  // Phase 3C.3 — the reminder defaults are booleans and a list, not strings, so
  // they cannot ride in `draft` (which is compared by trimmed string). They are
  // part of the SAME form and the SAME PATCH; only the diffing differs.
  const [reminders, setReminders] = useState({ enabled: true, daysBefore: [] });
  const [members, setMembers] = useState([]);
  // Phase 3B — the informational next-number preview. Nothing is reserved.
  const [nextReference, setNextReference] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const canEdit = !!settings?.can_edit;
  const editableFields = useMemo(
    () => (canEdit ? [...BASIC_FIELDS, ...CONTACT_FIELDS, ...EMAIL_FIELDS, ...NUMBERING_FIELDS] : []),
    [canEdit],
  );

  const applySettings = useCallback((data) => {
    setSettings(data);
    setDraft(draftFrom(data));
    setReminders({
      // Absent for a non-owner: the backend narrows the projection, so the card
      // is simply not rendered rather than rendered with invented values.
      enabled: data?.quotation_reminders_enabled_by_default !== false,
      daysBefore: Array.isArray(data?.quotation_reminder_days_before)
        ? data.quotation_reminder_days_before
        : [],
    });
  }, []);

  const load = useCallback(async () => {
    setFormError("");
    try {
      const r = await api.get("/organization/settings");
      applySettings(r.data);
    } catch (err) {
      reportError("company.loadSettings", err);
      setFormError(apiErrorMessage(err, t("common.error_generic")));
    }
    try {
      const m = await api.get("/organization/members");
      setMembers(m.data || []);
    } catch (err) {
      // Non-fatal: the company form still renders without the members table.
      reportError("company.loadMembers", err);
    }
    try {
      const n = await api.get("/quotations/next-reference");
      setNextReference(n.data?.reference_number || "");
    } catch (err) {
      // Non-fatal: the preview is informational only.
      reportError("company.nextReference", err);
    }
  }, [applySettings, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onOrg = () => load();
    window.addEventListener("quowada:orgChanged", onOrg);
    return () => window.removeEventListener("quowada:orgChanged", onOrg);
  }, [load]);

  const changed = useMemo(() => {
    if (!settings) return {};
    const out = {};
    editableFields.forEach((field) => {
      const next = (draft[field] ?? "").trim();
      const current = settings[field] ?? "";
      if (next !== current) out[field] = next === "" ? null : next;
    });
    return out;
  }, [draft, settings, editableFields]);

  const remindersSupported =
    !!settings && settings.quotation_reminder_days_before !== undefined;

  const reminderChanged = useMemo(() => {
    if (!settings || !canEdit || !remindersSupported) return null;
    const currentEnabled = settings.quotation_reminders_enabled_by_default !== false;
    const currentOffsets = settings.quotation_reminder_days_before || [];
    const out = {};
    if (reminders.enabled !== currentEnabled) {
      out.quotation_reminders_enabled_by_default = reminders.enabled;
    }
    if (!sameOffsets(reminders.daysBefore, currentOffsets)) {
      out.quotation_reminder_days_before = reminders.daysBefore;
    }
    return Object.keys(out).length ? out : null;
  }, [settings, canEdit, remindersSupported, reminders]);

  const dirty = Object.keys(changed).length > 0 || !!reminderChanged;

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const setField = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setNotice("");
  }, []);

  const save = useCallback(
    async (event) => {
      event.preventDefault();
      if (inFlight.current || !canEdit) return;
      setNotice("");
      setFormError("");
      const found = validateSettings(draft, editableFields);
      const reminderError = reminderChanged
        ? validateOffsets(reminders.daysBefore)
        : null;
      if (reminderError) found.quotation_reminder_days_before = reminderError;
      if (Object.keys(found).length) {
        setErrors(found);
        return;
      }
      setErrors({});
      if (!dirty) {
        setNotice(t("company.no_changes"));
        return;
      }
      inFlight.current = true;
      setSaving(true);
      try {
        const r = await api.patch("/organization/settings", {
          ...changed,
          ...(reminderChanged || {}),
        });
        applySettings(r.data);
        notifyBrandingChanged();
        setNotice(t("company.saved"));
      } catch (err) {
        reportError("company.save", err);
        const fields = apiFieldErrors(err);
        if (fields) setErrors(fields);
        else setFormError(apiErrorMessage(err, t("common.error_generic")));
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [
      applySettings,
      canEdit,
      changed,
      dirty,
      draft,
      editableFields,
      reminderChanged,
      reminders.daysBefore,
      t,
    ],
  );

  const onLogoChanged = useCallback(
    (data) => {
      applySettings(data);
      notifyBrandingChanged();
    },
    [applySettings],
  );

  const field = (name, extra = {}) => (
    <CompanyField
      key={name}
      id={`company-${name}`}
      testId={`company-${name}`}
      label={t(`company.${name === "default_currency" ? "currency" : name}`)}
      value={draft[name]}
      onChange={(v) => setField(name, v)}
      disabled={!canEdit}
      error={errors[name]}
      maxLength={MAX_LENGTHS[name]}
      {...extra}
    />
  );

  return (
    <Layout title={t("company.title")}>
      <div className="h1">{t("company.title")}</div>
      <div style={{ height: 20 }} />

      {settings && !canEdit && (
        <div
          className="qw-card"
          style={{
            background: "#fef3c7",
            borderColor: "#fde68a",
            color: "#78350f",
            marginBottom: 16,
          }}
          data-testid="company-readonly-notice"
        >
          {t("company.readonly_notice")}
        </div>
      )}

      {!settings ? (
        <div className="qw-empty" data-testid="company-loading">
          {t("common.loading")}
        </div>
      ) : (
        <form onSubmit={save} noValidate data-testid="company-settings-form">
          <div className="qw-card">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {t("company.section_basic")}
            </div>
            <div className="qw-grid cols-2">
              {field("name", { required: true })}
              {field("legal_name")}
              {field("eik", { inputMode: "numeric" })}
              {field("vat_number")}
              {field("default_currency")}
            </div>
          </div>

          <div style={{ height: 16 }} />
          <div className="qw-card">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {t("company.section_contacts")}
            </div>
            <div className="qw-grid cols-2">
              {field("phone", { inputMode: "tel" })}
              {field("website")}
              {field("address")}
            </div>
          </div>

          <div style={{ height: 16 }} />
          <div className="qw-card" data-testid="company-numbering-section">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {t("company.section_numbering")}
            </div>
            <div className="qw-help" style={{ marginBottom: 12 }}>
              {t("company.numbering_help")}
            </div>
            <div className="qw-grid cols-2">
              {field("quotation_number_prefix")}
              {nextReference && (
                <div className="qw-field">
                  <div style={{ fontWeight: 600 }} data-testid="company-next-reference">
                    {t("company.next_reference", { reference: nextReference })}
                  </div>
                  <div className="qw-help">{t("company.next_reference_hint")}</div>
                </div>
              )}
            </div>
          </div>

          {remindersSupported && canEdit && (
            <>
              <div style={{ height: 16 }} />
              <ReminderScheduleCard
                enabled={reminders.enabled}
                daysBefore={reminders.daysBefore}
                canEdit={canEdit}
                error={errors.quotation_reminder_days_before}
                onChange={(next) => {
                  setReminders(next);
                  setErrors((prev) =>
                    prev.quotation_reminder_days_before
                      ? { ...prev, quotation_reminder_days_before: undefined }
                      : prev,
                  );
                  setNotice("");
                }}
              />
            </>
          )}

          <div style={{ height: 16 }} />
          <div className="qw-card" data-testid="company-email-section">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              {t("company.section_email")}
            </div>
            <div className="qw-help" style={{ marginBottom: 12 }}>
              {t("company.routing_help")}
            </div>
            {canEdit ? (
              <div className="qw-grid">
                {field("reply_to_email", {
                  type: "email",
                  help: t("company.reply_to_help"),
                })}
                {field("email_signature", {
                  multiline: true,
                  help: t("company.signature_help"),
                })}
              </div>
            ) : (
              <div className="qw-help" data-testid="company-email-readonly">
                {t("company.email_readonly_notice")}
              </div>
            )}
            <div className="qw-help" style={{ marginTop: 8 }}>
              {t("company.sender_notice")}
            </div>
          </div>

          {formError && (
            <div className="qw-error" style={{ marginTop: 12 }} data-testid="company-error">
              {formError}
            </div>
          )}
          {notice && (
            <div
              className="qw-help"
              style={{ color: "#059669", marginTop: 12 }}
              data-testid="company-saved"
            >
              {notice}
            </div>
          )}
          {canEdit && dirty && (
            <div className="qw-help" style={{ marginTop: 8 }} data-testid="company-unsaved">
              {t("company.unsaved")}
            </div>
          )}

          {canEdit && (
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                type="submit"
                className="qw-btn primary"
                disabled={saving}
                data-testid="company-save"
              >
                {saving ? t("company.saving") : t("company.save")}
              </button>
            </div>
          )}
        </form>
      )}

      <div style={{ height: 16 }} />
      {settings && (
        <CompanyLogoCard
          settings={settings}
          canEdit={canEdit}
          onChanged={onLogoChanged}
        />
      )}

      <div style={{ height: 16 }} />
      <div className="qw-card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("settings.members")}</div>
        <div className="qw-table-wrap" tabIndex={0} role="region" aria-label={t("settings.members")}>
          <table className="qw-table" data-testid="members-table">
            <thead>
              <tr>
                <th>{t("auth.full_name")}</th>
                <th>{t("settings.role")}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td data-testid={`member-name-${m.id}`}>
                    {m.display_label ||
                      m.profiles?.full_name?.trim() ||
                      t("quotations.owner_user_fallback")}
                  </td>
                  <td>
                    {m.role === "owner" ? t("settings.role_owner") : t("settings.role_sales")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
